<script>

import {axios} from '../../mixins';
import {Alerts} from '../../mixins/Alerts.js';
import MapBox from './../Geography/MapBox.vue';
import FarmAddressAutofill from './FarmAddressAutofill.vue';
import ChildrenList from "../ChildrenList.vue";
import EnrollmentHU from "./enrollmentHU.vue";
import {escapeHtml} from "@vue/shared";

export default {

  name: 'FarmsForm',

  mixins: [axios],

  components: {EnrollmentHU, ChildrenList, FarmAddressAutofill, MapBox},

  props: {
    resource: {
      required: true,
      type: Object,
    },
    locked: {
      required: false,
      type: Boolean,
      default: true,
    },
    mapboxToken: {
      required: true,
      type: String,
    },
    esriToken: {
      required: true,
      type: String,
    },
    alerts: {
      required: false,
      type: Alerts,
    },
    questionsData: {
      required: false,
      type: Object,
      default: null,
    },
    cropUseData: {
      required: false,
      type: Object,
      default: null,
    }
  },

  watch: {
    resource() {
      this.resourceForm = {...this.resource.data};
      this.resourceForm.hu_selections = JSON.parse(this.resourceForm.hu_selections)
      this.boundary = !this.boundary ? this.resource.data.boundary : this.boundary
    }
  },

  data() {
    return {
      resourceForm: {},
      editableClasses: 'bg-secondary italic rounded border border-grey-lighter w-full py-2 px-3 leading-tight focus:outline-none text-md',
      editableClassesSelect: 'bg-secondary italic rounded border border-grey-lighter w-full py-2 px-3 leading-tight focus:outline-none focus:ring',
      editableClassesLight: 'bg-secondary-light italic rounded border border-grey-lighter w-full py-2 px-3 leading-tight focus:outline-none focus:ring',
      boundary: null,
      submitting: false,
      confirm: false,
      // Create array to use as checkbox v-model
      cropUse: [],
    };
  },

  computed: {
    isCreating() {
      return this.resource.id === null
    }
  },

  mounted() {
    window.addEventListener("keydown", this.keyListener);
    window.setInterval(() => {
      this.checkForSubmission()
    }, 1000)

    this.resourceForm = {...this.resource.data}

    // Set crop use array
    if (this.resourceForm.crop_use) {
      this.cropUse = JSON.parse(this.resourceForm.crop_use)
    } else {
      this.cropUse = [];
    }

    // Set boundary
    this.boundary = !this.boundary ? this.resource.data.boundary : this.boundary
  },

  methods: {
    submitResource() {
      this.submitting = true
      let dataToSubmit = {...this.resource}
      this.resourceForm.crop_use = this.cropUse
      dataToSubmit.data = this.resourceForm
      if (this.isCreating) {
        this.$emit('store', dataToSubmit, 'farms')
      } else {
        this.$emit('update', dataToSubmit, 'farms')
      }
    },

    deleteResource() {
      this.resource.type = 'farms'
      this.$emit('delete', this.resource)
    },

    confirmClose(cancel) {
      this.confirm = false;
      if (!cancel) {
        this.deleteResource()
      }
    },

    updateState(state) {
      this.resourceForm.state = state
    },

    fillContactInfo() {
      let route = '/account?user=' + this.resource.data.user_id;
      this.get(route, { headers: { 'Accept' : 'application/json' }}).then((response) => {
        this.resourceForm.farm_contact_name = response.data.user.name
        this.resourceForm.farm_contact_phone = response.data.user.phone
        this.resourceForm.farm_contact_email = response.data.user.email
      })
    },

    checkForSubmission() {
      if (!this.alerts.isEmpty()) {
        this.submitting = false
      }
    },

    cancel() {
      this.$emit('getData', this.resource.id ?? null)
    }
  },
};

</script>

<template>
  <div class="p-4 flex flex-row">
    <div class="flex flex-col flex-1 mr-2">
      <div class="text-2xl font-bold py-3 border-b-2 border-grey">
        Farm{{ isCreating ? '' : ': ' + resource.data.name }}
      </div>
      <div v-if="this.isCreating">
        <slot name="create-farm"></slot>
      </div>
      <div class="py-4 flex flex-col">
        <div v-if="alerts.getMessage()" class="text-green">
          <i class="fas fa-check-circle mr-2"></i>
          {{ alerts.getMessage() }}
        </div>
        <form @submit.prevent="submitResource" method="post" class="text-sm">

          <label for="user_id" hidden>User</label>
          <input id="user_id" type="number" name="user_id" :value="resourceForm.user_id" hidden>

          <div class="flex flex-row flex-wrap flex-1 items-center my-1">
            <label for="name" class="font-semibold leading-none mb-1">Legal Entity <br>
              <span class="text-md font-medium">Enter the individual or business as it appears on a W9 or tax return.</span>
            </label>

            <input name="name"
                   v-model="resourceForm.name"
                   :disabled="locked"
                   :class="editableClasses"
                   type="text">
            <div class="text-red">
              {{ alerts.getError('name') }}
            </div>
          </div>

          <FarmAddressAutofill
              :address="resourceForm.address"
              :city="resourceForm.city"
              :state="resourceForm.state"
              :zipcode="resourceForm.zipcode"
              :county="resourceForm.county"
              :locked="locked"
              :mapbox-token="mapboxToken"
              :esri-token="esriToken"
              :alerts="alerts"
              :editable-classes="editableClasses"
              :editable-classes-select="editableClassesSelect"
              @update:address="resourceForm.address = $event"
              @update:city="resourceForm.city = $event"
              @update:state="resourceForm.state = $event"
              @update:zipcode="resourceForm.zipcode = $event"
              @update:county="resourceForm.county = $event"
          />

          <div class="flex flex-row flex-wrap flex-1 items-center my-1">
            <label for="total_acres_farmed" class="font-semibold leading-none mb-1">
              Total Acres Farmed
            </label>

            <input name="total_acres_farmed"
                   v-model="resourceForm.total_acres_farmed"
                   :disabled="locked"
                   :class="editableClasses"
                   type="number"
                   step="0.1"
            >
            <div class="text-red">
              {{ alerts.getError('total_acres_farmed') }}
            </div>
          </div>

          <div class="flex flex-col flex-wrap flex-1 my-1">
            <div class="font-semibold text-lg">
              Contact Information
            </div>
            <div class="flex flex-col leading-tight bg-secondary rounded tile-shadow focus:outline-none focus:ring">
              <div class="px-2 pt-2">
                Please enter the information of the person who is the primary contact for the legal entity.
                Use the button below to fill using information of the user this farm will belong to.
                <button class="button button-wide mt-2 bg-green text-white hover:bg-green-dark focus:bg-green-darker font-bold disabled:cursor-not-allowed"
                        @click="this.fillContactInfo()" type="button" :disabled="locked">Fill With User Info
                </button>
              </div>
              <div class="p-2">
                <div class="flex flex-row flex-wrap flex-1 items-center my-1">
                  <label for="farm_contact_name" class="font-semibold leading-none mb-1">First and Last Name <br>
                  </label>
                  <input name="farm_contact_name"
                         v-model="resourceForm.farm_contact_name"
                         :disabled="locked"
                         :class="editableClassesLight"
                         type="text">
                  <div class="text-red">
                    {{ alerts.getError('farm_contact_name') }}
                  </div>
                </div>

                <div class="flex flex-row flex-wrap flex-1 items-center my-1">
                  <label for="farm_contact_email" class="font-semibold">Email</label>

                  <input name="farm_contact_email"
                         v-model="resourceForm.farm_contact_email"
                         :disabled="locked"
                         :class="editableClassesLight"
                         type="email">
                  <div class="text-red">
                    {{ alerts.getError('farm_contact_email') }}
                  </div>
                </div>

                <div class="flex flex-row flex-wrap flex-1 items-center my-1">
                  <label for="farm_contact_phone" class="font-semibold">Phone</label>

                  <input name="farm_contact_phone"
                         v-model="resourceForm.farm_contact_phone"
                         :disabled="locked"
                         :class="editableClassesLight"
                         type="text">
                  <div class="text-red">
                    {{ alerts.getError('farm_contact_phone') }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="flex flex-col flex-wrap flex-1 my-1">
            <div class="font-semibold text-lg">
              Crop Use
            </div>
            <div class="flex flex-col leading-tight bg-secondary rounded tile-shadow focus:outline-none focus:ring p-2">
              <div>
                {{ (this.cropUseData.value) }}
              </div>
              <template v-for="(option, index) in this.cropUseData.entries">
                <div class="mt-1">
                  <input class="cursor-pointer" type="checkbox" name="crop_use[]" :id="index" :value="index" v-model="this.cropUse"/>
                  <label :for="index" class="ml-1 my-1 cursor-pointer">{{ option }}</label>
                </div>
              </template>
            </div>
          </div>

          <div>
            <div class="font-semibold text-lg">
              Voluntary Demographic Questionnaire
            </div>
            <div>
              <enrollment-hu :questions-data="this.questionsData" v-model="resourceForm.hu_selections"></enrollment-hu>
            </div>
          </div>

          <div class="text-sm flex flex-row-reverse justify-between">
            <div v-if="this.submitting"
                 class="button button-wide bg-blue text-white hover:bg-blue-dark mt-2 font-bold">
              <span class="fas fa-spinner-third fa-spin"></span>
            </div>

            <button
                v-show="!this.submitting"
                :disabled="this.submitting || this.locked"
                class="button button-wide bg-blue text-white hover:bg-blue-dark focus:bg-blue-darker mt-2 font-bold disabled:cursor-not-allowed"
                type="submit" name="field-submit">
              <span class="mr-1"><i class="fas fa-floppy-disk"/></span>
              <span>Save</span>
            </button>

            <button
                :disabled="this.submitting"
                class="button button-wide bg-primary border-blue hover:bg-grey-lightest focus:bg-grey-lighter mt-2 font-bold disabled:cursor-not-allowed"
                type="button" @click="cancel">
              <span class="mr-1"><i class="fas fa-square-xmark"/></span>
              <span>Cancel</span>
            </button>

            <button
                v-if="!this.isCreating"
                :disabled="this.submitting || this.locked"
                class="button button-wide bg-red text-white hover:bg-red-dark focus:bg-red-darker mt-2 font-bold disabled:cursor-not-allowed"
                type="button" @click="this.confirm = true">
              <span class="mr-1"><i class="fas fa-trash"/></span>
              <span>Delete</span>
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- // Field Boundary map -->
    <div class="flex flex-col w-3/5 ml-2" v-if="!isCreating">

      <div class="mb-4">
        <MapBox ref="farm_map"
                :source="boundary"
                :draw="false"
                :token="mapboxToken"
                :esri-token="esriToken"
                :popups="true">
        </MapBox>
      </div>
    </div>

    <div v-if="this.confirm" class="fixed flex justify-center items-center top-0 bottom-0 left-0 right-0" @click="this.confirm = false">
      <div class="bg-primary p-4 w-1/6 text-center rounded tile-shadow" @click.stop="">
        <div>Are you sure you wish to delete this farm?</div>
        <div class="flex justify-between">
          <button class="button button-wide text-grey-dark hover:bg-grey-lightest focus:bg-grey-lighter mt-2 font-bold" @click="confirmClose(true)">
            Cancel
          </button>
          <button class="button button-wide text-white bg-red hover:bg-red-dark focus:bg-red-darker mt-2 font-bold" @click="confirmClose(false)">
            Yes, Delete
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
